import { company } from '~/company/Company';

import { ENDPOINT } from './constants';
import { RequestAPI } from './Requests';

/** Logistics provider backing a pickup point (e.g. 5Post). */
export type PickupPointProvider = 'fivepost' | (string & {});

/** Day-of-week key used in a pickup point's working-hours schedule. */
export type PickupPointWeekday =
  | 'MON'
  | 'TUE'
  | 'WED'
  | 'THU'
  | 'FRI'
  | 'SAT'
  | 'SUN';

/** A single day's opening hours for a pickup point. */
export interface PickupPointWorkHours {
  day: PickupPointWeekday;
  /** Opening time, "HH:mm:ss". */
  opensAt: string;
  /** Closing time, "HH:mm:ss". */
  closesAt: string;
  timezone: string;
  timezoneOffset: string;
}

/** Parcel-cell constraints of a pickup point (largest parcel it accepts). */
export interface PickupPointCellLimits {
  /** Max parcel weight, grams. */
  maxWeight: number;
  maxCellWidth: number;
  maxCellHeight: number;
  maxCellLength: number;
}

/** Structured address fields nested under a pickup point's attributes. */
export interface PickupPointAddress {
  house?: string;
  region?: string;
  zipCode?: string;
  metroStation?: string;
  cityType?: string;
  regionType?: string;
}

/** Rich metadata of a pickup point (subset of provider fields used by the UI). */
export interface PickupPointAttributes {
  phone?: string;
  shortAddress?: string;
  fullAddress?: string;
  /** Free-text hint for the customer (e.g. cashier location inside a store). */
  additional?: string;
  partnerName?: string;
  cardAllowed?: boolean;
  cashAllowed?: boolean;
  returnAllowed?: boolean;
  workHours?: PickupPointWorkHours[];
  cellLimits?: PickupPointCellLimits;
  address?: PickupPointAddress;
}

/** Estimated delivery time to a pickup point, in days. */
export interface PickupPointEta {
  sl?: number;
  minimalSl?: number;
  total_eta?: number;
  total_eta_min?: number;
  first_mile_eta?: number;
  first_mile_eta_min?: number;
}

/** A pickup point (ПВЗ) returned by the wms-connector service. */
export interface PickupPoint {
  /** Stable UUID; sent to the backend on order creation. */
  id: string;
  provider: PickupPointProvider;
  externalId: string;
  name: string;
  type: string;
  fullAddress: string;
  city: string;
  street: string;
  latitude: number;
  longitude: number;
  attributes: PickupPointAttributes;
  eta: PickupPointEta;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

/** Compact "HH:mm–HH:mm" working-hours summary from the first available day. */
export const formatPickupPointWorkHours = (point: PickupPoint): string => {
  const first = point.attributes.workHours?.[0];
  if (!first) {
    return '';
  }
  return `${first.opensAt.slice(0, 5)}–${first.closesAt.slice(0, 5)}`;
};

/** Open all day: the first schedule entry runs from 00:00 to 23:59 or wraps to 00:00. */
export const isRoundTheClock = (point: PickupPoint): boolean => {
  const first = point.attributes.workHours?.[0];
  if (!first) {
    return false;
  }
  const closesAt = first.closesAt.slice(0, 5);
  return (
    first.opensAt.startsWith('00:00') &&
    ['23:59', '00:00', '24:00'].includes(closesAt)
  );
};

/** Point types the customer sees as postamats; every other type is a staffed pickup point. */
export const POSTAMAT_TYPES = ['POSTAMAT', 'TOBACCO'];

export const isPostamatType = (type: string | undefined): boolean =>
  !!type && POSTAMAT_TYPES.includes(type);

/** Filters/pagination for the pickup-points list request. */
export interface PickupPointsParams {
  page?: { current: number; size: number };
  city?: string;
  street?: string;
  /** Bounding-box filter (south latitude). */
  minLat?: number;
  /** Bounding-box filter (north latitude). */
  maxLat?: number;
  /** Bounding-box filter (west longitude). */
  minLng?: number;
  /** Bounding-box filter (east longitude). */
  maxLng?: number;
}

interface PickupPointsListResponse {
  data: PickupPoint[];
}

/** Viewport request of the v2 endpoint; the bounding box is mandatory there. */
export interface PickupPointsMapParams {
  minLat: number;
  maxLat: number;
  minLng: number;
  maxLng: number;
  /** Point types to keep (`ISSUE_POINT`, `POSTAMAT`, …); empty means every type. */
  types?: string[];
}

/** Points the backend folded into one map tile at the response zoom. */
export interface PickupPointCluster {
  zoom: number;
  x: number;
  y: number;
  count: number;
  latitude: number;
  longitude: number;
}

export interface PickupPointsMapArea {
  zoom: number;
  expandThreshold: number;
  /** Points inside the bounding box, clustered ones included. */
  total: number;
  clusters: PickupPointCluster[];
  points: PickupPoint[];
}

interface PickupPointsMapResponse {
  data: PickupPointsMapArea;
}

interface PickupPointsRequestsInterface {
  getPickupPoints(
    params?: PickupPointsParams,
  ): Promise<PickupPointsListResponse>;
  getPickupPointsMap(
    params: PickupPointsMapParams,
  ): Promise<PickupPointsMapResponse>;
  /** NB: unlike the list endpoint, the by-id response is NOT wrapped in `data`. */
  getPickupPointById(id: string): Promise<PickupPoint>;
}

/**
 * Flattens nested pagination into the bracketed query keys the wms-connector
 * expects (`page[current]`, `page[size]`); empty/undefined values are dropped
 * by the query serializer. The service authorizes by `companyId` query param.
 */
const toQuery = (
  params?: PickupPointsParams,
): Record<string, string | number | undefined> => ({
  companyId: company.config.id,
  'page[current]': params?.page?.current,
  'page[size]': params?.page?.size,
  city: params?.city,
  street: params?.street,
  minLat: params?.minLat,
  maxLat: params?.maxLat,
  minLng: params?.minLng,
  maxLng: params?.maxLng,
});

/**
 * Built by hand: the shared serializer joins arrays with commas, which the v2
 * endpoint rejects — it wants `type[]` repeated per value.
 */
const toMapQuery = ({
  types = [],
  ...bounds
}: PickupPointsMapParams): string => {
  const query = new URLSearchParams({ companyId: String(company.config.id) });
  Object.entries(bounds).forEach(([key, value]) => {
    query.set(key, String(value));
  });
  types.forEach((type) => {
    query.append('type[]', type);
  });
  return query.toString();
};

/**
 * Serves pickup points from a static mock fixture (a real HTTP GET to
 * `/mocks/pickup-points.json`) instead of the live wms-connector service.
 * Set to `true` for local UI testing when the backend env lacks the endpoint.
 */
const USE_PICKUP_POINTS_MOCK = false;

const MOCK_URL = '/mocks/pickup-points.json';

const fetchMockPickupPoints = async (): Promise<PickupPoint[]> => {
  const response = await fetch(MOCK_URL);
  const json = (await response.json()) as PickupPointsListResponse;
  return json.data;
};

const includes = (value: string, query?: string) =>
  !query || value.toLowerCase().includes(query.toLowerCase());

const filterMockPickupPoints = (
  points: PickupPoint[],
  params?: PickupPointsParams,
): PickupPoint[] => {
  const filtered = points.filter((point) => {
    if (!includes(point.city, params?.city)) {
      return false;
    }
    if (!includes(point.street, params?.street)) {
      return false;
    }
    if (
      params?.minLat !== undefined &&
      params?.maxLat !== undefined &&
      params?.minLng !== undefined &&
      params?.maxLng !== undefined
    ) {
      return (
        point.latitude >= params.minLat &&
        point.latitude <= params.maxLat &&
        point.longitude >= params.minLng &&
        point.longitude <= params.maxLng
      );
    }
    return true;
  });

  const size = params?.page?.size;
  const current = params?.page?.current ?? 1;
  if (!size) {
    return filtered;
  }
  return filtered.slice((current - 1) * size, current * size);
};

export const PickupPointsRequests: PickupPointsRequestsInterface = {
  getPickupPoints: async (params) => {
    if (USE_PICKUP_POINTS_MOCK) {
      const all = await fetchMockPickupPoints();
      return { data: filterMockPickupPoints(all, params) };
    }
    return RequestAPI.get(ENDPOINT.pickupPoints.list, toQuery(params));
  },
  getPickupPointsMap: async (params) => {
    if (USE_PICKUP_POINTS_MOCK) {
      const all = await fetchMockPickupPoints();
      const points = filterMockPickupPoints(all, params).filter(
        (point) => !params.types?.length || params.types.includes(point.type),
      );
      return {
        data: {
          zoom: 0,
          expandThreshold: 0,
          total: points.length,
          clusters: [],
          points,
        },
      };
    }
    return RequestAPI.get(`${ENDPOINT.pickupPoints.map}?${toMapQuery(params)}`);
  },
  getPickupPointById: async (id) => {
    if (USE_PICKUP_POINTS_MOCK) {
      const all = await fetchMockPickupPoints();
      return all.find((point) => point.id === id) as PickupPoint;
    }
    return RequestAPI.get(ENDPOINT.pickupPoints.byId + id, {
      companyId: company.config.id,
    });
  },
};
